import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useAssistant } from '@/ai/assistant-context';
import { useSession } from '@/auth/session-context';
import { AppSidebar } from '@/components/app-sidebar';
import { AssistantDrawer } from '@/components/assistant/assistant-drawer';
import { CommandPalette } from '@/components/command-palette';
import { ImpersonationBanner } from '@/components/impersonation-banner';
import { MobileNavDrawer, MobileTopBar } from '@/components/mobile-nav';
import { OpeningSplash, WORKSPACE_SPLASH_LABEL } from '@/components/opening-splash';
import { PlanLimitDialog } from '@/components/plan-limit-dialog';
import { markWorkspaceReady } from '@/lib/perf';
import { useDispatch } from '@/store/use-dispatch';
import { useIngredientLinkBackfill } from '@/store/use-ingredient-link-backfill';
import { useHydrated } from '@/store/workspace-context';

const STORAGE_KEY = 'beerwright.sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * True when the event's target is a field that owns its own undo stack.
 *
 * Exported because it is the rule that decides whether ⌘Z reaches the command
 * log at all: the editor's own regression tests install the shortcut below
 * around a bare `RecipeEditor` (this shell needs a router, a session and the
 * assistant context to mount) and must apply the real rule, not a restatement
 * of it.
 */
export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
}

export function AppShell({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const hydrated = useHydrated();
  // The one-time link pass for lots and rows that predate ingredient links.
  // Lives in the shell because the shell is mounted exactly once per session
  // and already knows when the workspace is ready. See the hook for why it
  // also waits for sync.
  useIngredientLinkBackfill();
  const { undo, redo } = useDispatch();
  const { toggle: toggleAssistant } = useAssistant();
  const session = useSession();

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // Persistence is best-effort.
    }
  }, [collapsed]);

  // The E5 instrumentation (PLAN §10's repeat-visit budget). A *layout* effect,
  // so the mark is taken after React has committed the splash → route swap and
  // before the browser paints it: this is a claim about the DOM, not about a
  // state variable. See `lib/perf.ts` for why the app defines "interactive"
  // itself rather than leaning on a browser timing.
  useLayoutEffect(() => {
    if (hydrated) markWorkspaceReady();
  }, [hydrated]);

  // ⌘Z / ⇧⌘Z across the whole workspace — the command log makes this natural
  // (PLAN §4.1). Text fields keep their own native undo.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
      if (isTextEntry(event.target)) return;
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  // ⌘J toggles the assistant (PLAN §4.2). Registered here rather than in the
  // drawer so it works when the drawer is closed, which is the point.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'j') return;
      event.preventDefault();
      toggleAssistant();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toggleAssistant]);

  // The nav's width is the brewer's decision and nothing else's.
  //
  // Opening the assistant used to collapse it and restore it on close, with a
  // second flag remembering an expand they made in between — three pieces of
  // state for one width. That existed because the drawer docked beside the
  // editor from `md`, where sidebar plus assistant plus vitals left the recipe
  // nothing. It docks from `xl` now (see `assistant-drawer.tsx`), and below
  // that it covers the screen, so there is nothing underneath it to make room
  // for. Above it, the drawer's own width clamp keeps the editor's column.
  const toggleCollapsed = useCallback(() => {
    setCollapsed((previous) => !previous);
  }, []);

  return (
    // The three `data-slot`s are what the print rules in `styles.css` key off:
    // printing hides every child of the shell that is not the body, and every
    // child of the body that is not `<main>`, which strips the sidebar, the
    // mobile top bar, the assistant drawer and the palette in two selectors —
    // without any of those components having to know that printing exists.
    // The shell stacks, at every width. It is `app-body` below that runs
    // horizontally — sidebar, main, assistant — and this element must not, because
    // the impersonation banner is its child and a row would hand the banner a
    // *column* beside the app instead of a strip above it. That is precisely what
    // `md:flex-row` did here: correct on a phone, and on a desktop a banner down
    // the left-hand side with the whole application crushed into what was left.
    // The row it was reaching for already exists one level down.
    <div
      data-slot="app-shell"
      className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-background"
    >
      {/* Above everything, including the mobile top bar: an impersonated session
          has to be marked in a way that displaces the app rather than floating
          over it (PLAN-OPS Phase 4). Renders nothing at all for every ordinary
          session, which is all of them. */}
      <ImpersonationBanner />
      <MobileTopBar menuOpen={menuOpen} onMenuOpenChange={setMenuOpen} />
      {/* `--app-nav-width` is how wide the nav actually is right now, published
          for the things that have to position against it but cannot see it.
          A `fixed` element is laid out against the viewport, not against this
          row, so anything pinned to the bottom of the editor would otherwise
          start at x=0 and run underneath the sidebar. Zero below `md`, where
          there is no sidebar to clear, and the two `w-` values the sidebar
          itself uses above it. */}
      <div
        data-slot="app-body"
        className={`flex min-h-0 min-w-0 flex-1 overflow-hidden [--app-nav-width:0px] ${
          collapsed ? 'md:[--app-nav-width:4rem]' : 'md:[--app-nav-width:15rem]'
        }`}
      >
        <AppSidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
        <main
          data-slot="app-main"
          className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto pb-[env(safe-area-inset-bottom,0px)] md:pb-0"
        >
          {hydrated ? children : <OpeningSplash label={WORKSPACE_SPLASH_LABEL} />}
        </main>
        <AssistantDrawer />
      </div>
      <MobileNavDrawer menuOpen={menuOpen} onMenuOpenChange={setMenuOpen} />
      <CommandPalette />
      {/* Mounted once: a plan rejection arrives on a socket frame and has no
          idea what is on screen when it does. */}
      <PlanLimitDialog entitlement={session.entitlement} />
    </div>
  );
}
